import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { use, useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/RegionSummaryPageFilterSection';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { FieldFilter } from '@/api/DTO/field-option/fieldOption.interface';
import {
	useGetFieldOptionRegionDetailQuery,
	useGetFieldOptionRegionListQuery,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import { FormType } from '@/api/enum/formType.enum';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import EditRegionDialog from './components/EditRegionDialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useGetUser } from '@/hooks/useGetUser';
import { PermissionEnum } from '@/api/enum/common.enum';

interface Props {
	title?: string;
	type?: number;
}
export default function RegionSummaryPage(props: Props) {
	const { title, type } = props;
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const { fieldId, districtId, subDistrictId, ecvId, buildId } = useParams();
	const { userInfo } = useGetUser();

	const { data: districtDetails } = useGetFieldOptionRegionDetailQuery(
		districtId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 1 && !districtId,
		}
	);

	const { data: subDistrictDetails } = useGetFieldOptionRegionDetailQuery(
		subDistrictId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 2 && !subDistrictId,
		}
	);
	const { data: ecvDetails } = useGetFieldOptionRegionDetailQuery(ecvId || '', {
		refetchOnMountOrArgChange: true,
		skip: type !== 3 && !ecvId,
	});
	const { data: buildDetails } = useGetFieldOptionRegionDetailQuery(
		buildId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 4 && !buildId,
		}
	);

	const defaultPropertyFilter: FieldFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<FieldFilter> = defaultPropertyFilter;
	const methods = useForm<FieldFilter>({
		defaultValues,
	});
	const transformedSearchParams: FieldFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionRegionListQuery(
			{
				...transformedSearchParams,
				type: type ? type : 0,
				parentId:
					type === 1
						? districtId
						: type === 2
							? subDistrictId
							: type === 3
								? ecvId
								: type === 4
									? buildId
									: '',
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FieldFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'code',
			headerName: t('field.code'),
			flex: 20,
		},
		{
			field: 'nameEn',
			headerName: t('field.en'),
			flex: 20,
		},
		{
			field: 'nameZh',
			headerName: t('field.zh'),
			flex: 20,
		},

		{
			field: 'updatedAt',
			headerName: t('field.lastUpdated'),
			flex: 20,
		},

		{
			field: 'status',
			headerName: t('field.status'),
			flex: 20,
			renderCell: (params) => (
				<Chip
					color={params.row.status === 0 ? 'chipTertiary' : 'chipPrimary'}
					sx={{
						height: 'auto',
						minHeight: '24px',
						maxWidth: '100%',
						overflow: 'hidden',
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						padding: '4px 4px',
					}}
					label={
						<Box
							sx={{
								width: '100%',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							}}
						>
							{params.row.status === 0
								? t('field.active')
								: t('field.inActive')}
						</Box>
					}
				/>
			),
		},
		{
			field: 'actionedByName',
			headerName: t('field.actionedBy'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<EditRegionDialog
					parentId={
						type === 1
							? districtId || ''
							: type === 2
								? subDistrictId || ''
								: type === 3
									? ecvId || ''
									: type === 4
										? buildId || ''
										: ''
					}
					currentId={params.row.id}
					formType={FormType.edit}
					type={type ?? 0}
				/>
			),
		},
		{
			field: 'view',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				type != 4 && (
					<Link
						to={
							type === 1
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${params.row.id}`
								: type === 2
									? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${params.row.id}`
									: type === 3
										? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/build/${params.row.id}`
										: `/field-management/field-management-list/region/${fieldId}/district/${params.row.id}`
						}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{title &&
						(() => {
							if (![1, 2, 3, 4].includes(type ?? -1)) {
								return t('member.region');
							}

							const name =
								type === 1
									? districtDetails?.data.nameEn
									: type === 2
										? subDistrictDetails?.data.nameEn
										: type === 3
											? ecvDetails?.data.nameEn
											: buildDetails?.data.nameEn;

							return name ? `${title} (${name})` : title;
						})()}
				</Typography>
			</Box>
			<Box marginTop={1}>
				{userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementManageStatus
				) && (
					<Button
						endIcon={<DonutLargeOutlinedIcon />}
						onClick={() => {
							navigate(
								type === 1
									? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/status`
									: type === 2
										? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/status`
										: type === 3
											? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/status`
											: type === 4
												? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/build/${buildId}/status`
												: `/field-management/field-management-list/region/${fieldId}/status`
							);
						}}
						variant='contained'
						sx={{ marginRight: '12px' }}
					>
						{t('field.status')}
					</Button>
				)}

				{userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementReOrder
				) && (
					<Button
						endIcon={<SwapVertOutlinedIcon />}
						onClick={() => {
							navigate(
								type === 1
									? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/order`
									: type === 2
										? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/order`
										: type === 3
											? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/order`
											: type === 4
												? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/build/${buildId}/order`
												: `/field-management/field-management-list/region/${fieldId}/order`
							);
						}}
						variant='contained'
						sx={{ marginRight: '12px' }}
					>
						{t('field.reOrderOption')}
					</Button>
				)}

				<EditRegionDialog
					formType={FormType.create}
					type={type ?? 0}
					parentId={
						type === 1
							? districtId || ''
							: type === 2
								? subDistrictId || ''
								: type === 3
									? ecvId || ''
									: type === 4
										? buildId || ''
										: ''
					}
				/>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					type='submit'
					variant='contained'
					onClick={() => {
						navigate(
							type === 1
								? `/field-management/field-management-list/region/${fieldId}`
								: type === 2
									? `/field-management/field-management-list/region/${fieldId}/district/${districtId}`
									: type === 3
										? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}`
										: type === 4
											? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}`
											: `/field-management/field-management-list`
						);
					}}
				>
					{t('button.back')}
				</Button>
			</Box>
		</>
	);
}
